// includes/auth/apiEmailMarketing.js
//
// API layer for the Email Marketing module. Two conventions, mirrored from
// includes/auth/monitoringApi.js (itself modeled on apiDbAdmin.js /
// Animationsettingsapi.js):
//   - dashboard/campaign endpoints: plain JSON, apiFetch() shape (same as
//     apiPageBuilder.js / apiHowWeDoIt.js).
//   - /email-marketing/settings* endpoints: encryptObjElakay-wrapped
//     responses, same fetchAndDecrypt() shape as monitoringApi.js, since
//     Email Marketing Settings is a tab inside the existing Configuration
//     Settings surface.
//
// Extended phase by phase as the module grows (campaigns, builder,
// audience, pricing, send, analytics) -- settings + permissions first.

import { elakay_API } from "../../config";
import { isAuthenticated } from "../auth";
import { decryptData } from "../auth/elakayCryptage";
import { prepareUpload } from "../../utils/uploadPolicy";
import { readUploadResponse, withUploadRetry } from "../../utils/uploadResponse";

const getToken = () => isAuthenticated()?.token || "";

export const buildQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.set(key, value);
    }
  });
  return query.toString();
};

export const apiFetch = async (path, { method = "GET", body, token } = {}) => {
  const headers = { Accept: "application/json", "Content-Type": "application/json" };
  const resolvedToken = token || getToken();
  if (resolvedToken) headers.Authorization = `Bearer ${resolvedToken}`;

  const response = await fetch(`${elakay_API}${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const serverMessage = data.error || data.message;
    const message = serverMessage
      ? `${serverMessage} (HTTP ${response.status})`
      : `Email Marketing API error ${response.status}`;
    const error = new Error(message);
    error.status = response.status;
    error.payload = data;
    throw error;
  }
  return data;
};

const fetchAndDecrypt = async (path, { method = "GET", body, token } = {}) => {
  const headers = { Accept: "application/json", "Content-Type": "application/json" };
  const resolvedToken = token || getToken();
  if (resolvedToken) headers.Authorization = `Bearer ${resolvedToken}`;

  const response = await fetch(`${elakay_API}${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const encryptedData = await response.json();
  const decrypted = await decryptData(encryptedData);
  if (decrypted && decrypted.success === false) {
    throw new Error(decrypted.error || "Email Marketing settings request failed");
  }
  return decrypted;
};

// Real bug fix: `fieldName` used to be hardcoded to "file" here, but
// elakay_routes/emailMarketing.js's media/upload route is `multer().single(
// "image")` -- any other field name makes Multer reject the whole request
// with "Unexpected field" before the controller ever runs (a 400 with no
// campaign/HTML/audience explanation at all, easy to misdiagnose as a
// confirm-send-adjacent bug). Defaults to "file" so every OTHER existing
// caller of this generic helper keeps sending exactly what it always has;
// only uploadEmailImageAPI below passes the real "image" field name Multer
// expects.
export const uploadFile = async (endpoint, file, extraFields = {}, token, fieldName = "file", feature = null) => {
  // Checked (and compressed when heavy) before sending; see uploadPolicy.
  const prepared = feature ? await prepareUpload(feature, file) : file;
  const formData = new FormData();
  formData.append(fieldName, prepared);
  Object.entries(extraFields).forEach(([key, value]) => formData.append(key, value));

  const headers = { Accept: "application/json" };
  const resolvedToken = token || getToken();
  if (resolvedToken) headers.Authorization = `Bearer ${resolvedToken}`;

  // Throws a readable French message, also for a proxy HTML 413 page.
  return withUploadRetry(async () => readUploadResponse(await fetch(`${elakay_API}${endpoint}`, { method: "POST", headers, body: formData })));
};

// ---- Configuration Settings -> Email Marketing (encrypted) -------------

export const getEmailMarketingSettingsAPI = (token) => fetchAndDecrypt("/email-marketing/settings", { token });

export const getEmailPreviewDisplayAPI = (token) => apiFetch("/email-marketing/preview-display", { token });

// Read-only Product Card registry (spec: "Add Product Card and Change Card
// Type must consume the same registry") -- the admin's own enabled
// Product Display presets, or the built-in starter list when nothing is
// configured. Plain (unencrypted) apiFetch, matching getEmailPreviewDisplayAPI
// just above: a campaign creator may read this to populate a Card Type
// picker, but (like preview-display) cannot access or mutate the rest of
// the settings document via this endpoint.
export const getProductDisplayPresetsAPI = (token) => apiFetch("/email-marketing/product-display-presets", { token });

export const updateEmailMarketingSettingsSectionAPI = (section, sectionData, token) =>
  fetchAndDecrypt(`/email-marketing/settings/${section}`, { method: "PATCH", body: sectionData, token });

export const previewEmailMarketingPriceAPI = (quantity, token) =>
  apiFetch("/email-marketing/settings/price-preview", { method: "POST", body: { quantity }, token });

export const createEmailMarketingPromotionAPI = (payload, token) =>
  fetchAndDecrypt("/email-marketing/settings/promotions", { method: "POST", body: payload, token });

export const updateEmailMarketingPromotionAPI = (promoId, payload, token) =>
  fetchAndDecrypt(`/email-marketing/settings/promotions/${promoId}`, { method: "PUT", body: payload, token });

export const deleteEmailMarketingPromotionAPI = (promoId, token) =>
  fetchAndDecrypt(`/email-marketing/settings/promotions/${promoId}`, { method: "DELETE", token });

// ---- My permissions (plain JSON, used everywhere in the module) --------

export const getMyEmailMarketingPermissionsAPI = (token) => apiFetch("/email-marketing/permissions/me", { token });

export const getEmailMarketingGlobalUsageAPI = (token) => apiFetch("/email-marketing/admin/usage", { token });

// ---- Campaigns (plain JSON) ---------------------------------------------

export const getCampaignTypesAPI = (token) => apiFetch("/email-marketing/campaign-types", { token });

export const listCampaignsAPI = (params = {}, token) => {
  const query = buildQuery(params);
  return apiFetch(`/email-marketing/campaigns${query ? `?${query}` : ""}`, { token });
};

export const createCampaignAPI = (payload, token) =>
  apiFetch("/email-marketing/campaigns", { method: "POST", body: payload, token });

export const getCampaignAPI = (id, token) => apiFetch(`/email-marketing/campaigns/${id}`, { token });

export const updateCampaignNameAPI = (id, payload, token) =>
  apiFetch(`/email-marketing/campaigns/${id}`, { method: "PATCH", body: payload, token });

export const deleteCampaignAPI = (id, token) => apiFetch(`/email-marketing/campaigns/${id}`, { method: "DELETE", token });

// "Duplicate" (reuse a Sent -- or any other -- campaign): creates a fully
// independent new Draft, never touching the original's stats/history.
export const duplicateCampaignAPI = (id, token) => apiFetch(`/email-marketing/campaigns/${id}/duplicate`, { method: "POST", token });

// ---- Template Library (30-template brief) --------------------------------

export const getEmailTemplatesAPI = (token) => apiFetch("/email-marketing/templates", { token });

export const getTemplatePreviewHtmlAPI = (key, token) => apiFetch(`/email-marketing/templates/${key}/preview`, { token });

export const applyCampaignTemplateAPI = (id, templateKey, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/apply-template`, { method: "POST", body: { templateKey }, token });

export const setCampaignPresentationAPI = (id, payload, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/presentation`, { method: "PATCH", body: payload, token });

// ---- Page Builder (plain JSON) ------------------------------------------

export const saveCampaignBuilderAPI = (id, blocks, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/builder`, { method: "PATCH", body: { blocks }, token });

// mode: "generic" (sample sender + recipient) | "sender" (your real sender data)
// | "recipient" (+ recipient = a user's publicId/id you are authorized for).
export const getCampaignPreviewHtmlAPI = (id, token, { mode, recipient } = {}) => {
  const params = new URLSearchParams();
  if (mode) params.set("mode", mode);
  if (recipient) params.set("recipient", recipient);
  const qs = params.toString();
  return apiFetch(`/email-marketing/campaigns/${id}/preview${qs ? `?${qs}` : ""}`, { token });
};

// ---- Template Library (Elakay Templates = Family masters / My Templates) ----
const TL = "/email-marketing/template-library";
export const listTemplateLibraryAPI = (scope, token) => apiFetch(`${TL}?scope=${scope}`, { token });
export const createLibraryTemplateAPI = (payload, token) => apiFetch(TL, { method: "POST", body: payload, token });
export const updateLibraryTemplateAPI = (id, payload, token) => apiFetch(`${TL}/${id}`, { method: "PATCH", body: payload, token });
export const deleteLibraryTemplateAPI = (id, token) => apiFetch(`${TL}/${id}`, { method: "DELETE", token });
export const publishLibraryTemplateAPI = (id, allowedRoles, token) => apiFetch(`${TL}/${id}/publish`, { method: "POST", body: { allowedRoles }, token });
export const unpublishLibraryTemplateAPI = (id, token) => apiFetch(`${TL}/${id}/unpublish`, { method: "POST", token });
export const duplicateLibraryTemplateAPI = (id, token) => apiFetch(`${TL}/${id}/duplicate`, { method: "POST", body: {}, token });
export const applyLibraryTemplateAPI = (id, campaignId, token) => apiFetch(`${TL}/${id}/use`, { method: "POST", body: { campaignId }, token });
export const startTemplateEditSessionAPI = (id, token) => apiFetch(`${TL}/${id}/edit-session`, { method: "POST", token });
export const saveLibraryTemplateContentAPI = (id, fromCampaignId, token) => apiFetch(`${TL}/${id}/content`, { method: "PUT", body: { fromCampaignId }, token });
export const getLibraryTemplatePreviewAPI = (id, mode, token) => apiFetch(`${TL}/${id}/preview?mode=${mode || "generic"}`, { token });
// Template Designer workspace (gated server-side by canAccessTemplateDesigner).
export const startTemplateDesignerSessionAPI = (token, { fresh = false } = {}) =>
  apiFetch("/email-marketing/template-designer/session", { method: "POST", body: { fresh }, token });
export const saveCampaignAsTemplateAPI = (campaignId, payload, token) => apiFetch(`/email-marketing/campaigns/${campaignId}/save-as-template`, { method: "POST", body: payload, token });
export const setCampaignSourcePriorityAPI = (id, priority, token) => apiFetch(`/email-marketing/campaigns/${id}/source-priority`, { method: "PATCH", body: { priority }, token });

// testEmail is optional -- the backend falls back to the requester's own
// account email when omitted (unchanged behavior for existing callers).
export const sendTestEmailAPI = (id, testEmail, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/test-send`, { method: "POST", body: { testEmail: testEmail || undefined }, token });

// ---- HTML Source (Family-only view/edit) ---------------------------------

export const getCampaignHtmlSourceAPI = (id, token) => apiFetch(`/email-marketing/campaigns/${id}/html-source`, { token });

// Multilingual content: one JSON document per language rendered into
// {{content.<key>}} tokens in each recipient's profile language.
export const getEmailContentSettingsAPI = (token) => apiFetch("/email-marketing/content-settings", { token });
export const getCampaignLocalizedContentAPI = (id, token) => apiFetch(`/email-marketing/campaigns/${id}/localized-content`, { token });
export const saveCampaignLocalizedContentAPI = (id, body, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/localized-content`, { method: "PUT", body, token });
export const previewCampaignLocalizedContentAPI = (id, body, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/localized-content/preview`, { method: "POST", body, token });

export const saveCampaignHtmlSourceAPI = (id, html, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/html-source`, { method: "PATCH", body: { html }, token });

export const restoreCampaignGeneratedHtmlAPI = (id, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/html-source/restore`, { method: "POST", token });

export const uploadEmailImageAPI = (file, token) => uploadFile("/email-marketing/media/upload", file, {}, token, "image", "emailImage");

// ---- Select Content / Product Intelligence / AI assist -----------------

export const resolveCampaignContentAPI = (id, payload, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/resolve-content`, { method: "POST", body: payload, token });

// Add Product (Product Grid module): resolves ids -> real, current card
// data only -- never saves the campaign (see resolveProductGridItems in
// campaignController.js). The caller merges the result into one block's
// content.products and saves through the normal builder autosave.
export const resolveProductGridItemsAPI = (id, productIds, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/resolve-product-grid-items`, { method: "POST", body: { productIds }, token });

export const getEmailContentOwnersAPI = (params = {}, token) => {
  const query = buildQuery(params);
  return apiFetch(`/email-marketing/content/owners${query ? `?${query}` : ""}`, { token });
};

export const getEmailContentProductsAPI = (params = {}, token) => {
  const ownerId = params.ownerId && typeof params.ownerId === "object"
    ? params.ownerId._id || params.ownerId.id || params.ownerId.publicId || params.ownerId.ownerId || ""
    : params.ownerId;
  const query = buildQuery({ ...params, ownerId });
  return apiFetch(`/email-marketing/content/products${query ? `?${query}` : ""}`, { token });
};

export const getEmailContentTutorialsAPI = (params = {}, token) => {
  const query = buildQuery(params);
  return apiFetch(`/email-marketing/content/tutorials${query ? `?${query}` : ""}`, { token });
};

export const getEmailContentShopAPI = (ownerId, token) => {
  const resolvedOwnerId = ownerId && typeof ownerId === "object"
    ? ownerId._id || ownerId.id || ownerId.publicId || ownerId.ownerId || ""
    : ownerId;
  const query = buildQuery({ ownerId: resolvedOwnerId });
  return apiFetch(`/email-marketing/content/shop${query ? `?${query}` : ""}`, { token });
};

export const analyzeMyProductsAPI = (token) => apiFetch("/email-marketing/intelligence/analyze", { token });

export const aiAssistAPI = (type, input, token) =>
  apiFetch("/email-marketing/ai/assist", { method: "POST", body: { type, input }, token });

// ---- Audience Targeting (never returns emails/raw lists) ----------------

export const saveCampaignAudienceAPI = (id, filters, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/audience`, { method: "POST", body: filters, token });

// Manual Emails (new audience mode, additive alongside Database Audience
// above): audienceMode is its own tiny PATCH (mirrors setPresentation's
// convention) so switching modes never clears/touches the other mode's
// saved config.
export const setCampaignAudienceModeAPI = (id, audienceMode, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/audience-mode`, { method: "PATCH", body: { audienceMode }, token });

// Full-list replace -- the frontend keeps the chip list in local state and
// saves the whole array here (debounced) on every add/remove, never one
// request per address (see campaignController.saveManualRecipients).
export const saveCampaignManualRecipientsAPI = (id, emails, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/manual-recipients`, { method: "PATCH", body: { emails }, token });

export const getCampaignAudienceCountAPI = (id, token) => apiFetch(`/email-marketing/campaigns/${id}/audience/count`, { token });

export const estimateCampaignAudienceAPI = (id, filters, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/audience/estimate`, { method: "POST", body: filters, token });

// Spoken Language "Matching Users" (Email Language Settings): real counts of
// users.language.code for the DRAFT selection + eligibility checkboxes --
// { matchingUsers, byLanguage: { en, es, fr, ht, pt }, unknown, allLanguages }.
export const getSpokenLanguageCountAPI = (id, { spokenLanguages = [], eligibilityFilters = {} } = {}, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/audience/spoken-language-count`, { method: "POST", body: { spokenLanguages, eligibilityFilters }, token });

export const getCampaignAudiencePreviewSampleAPI = (id, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/audience/preview-sample`, { token });

// "View Users" modal (spec §22-27): the campaign OWNER paging through
// their OWN campaign's real recipient candidates -- unlike the two calls
// above, this DOES return real {_id, email, firstname, lastname} rows, the
// same shape estimateAudience already returns via includeUsers, just
// paginated server-side. `tab` is "raw" (pre-eligibility, "Users Found") or
// "eligible" (post-eligibility, "Eligible Users").
export const getCampaignAudienceUsersPageAPI = (id, { tab = "eligible", page = 1, limit = 50 } = {}, token) => {
  const query = buildQuery({ tab, page, limit });
  return apiFetch(`/email-marketing/campaigns/${id}/audience/users${query ? `?${query}` : ""}`, { token });
};

// Manual Selection (View Users' checkbox column): persists immediately on
// every change (never batched into "Save audience") so the selection
// survives a page change / re-estimate / draft update even if the creator
// never revisits the audience section. `_id`-only payload -- never email.
export const updateCampaignRecipientSelectionAPI = (id, recipientSelection, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/audience/selection`, { method: "PATCH", body: recipientSelection, token });

// ---- Smart Audience Filters (Select Content -> Choose Audience) ---------

export const getSmartAudienceSourcesAPI = (id, params = {}, token) => {
  const query = buildQuery(params);
  return apiFetch(`/email-marketing/campaigns/${id}/audience/smart-sources${query ? `?${query}` : ""}`, { token });
};

export const runSmartAudienceSearchAPI = (id, payload, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/audience/smart-search`, { method: "POST", body: payload, token });

// ---- Pricing (server-computed only) -------------------------------------

// Quantity-first: computed straight from a requested count, before any
// audience resolution -- see "Quantity & Calculate Price".
export const getRequestedPriceAPI = (id, requestedRecipientCount, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/requested-price`, { method: "POST", body: { requestedRecipientCount }, token });

export const getCampaignPriceAPI = (id, token) => apiFetch(`/email-marketing/campaigns/${id}/price`, { method: "POST", token });

// ---- Send integration ----------------------------------------------------

export const getSenderIdentitiesAPI = (token) => apiFetch("/email-marketing/sender-identities", { token });

export const setCampaignSenderAPI = (id, fromEmail, replyTo, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/sender`, { method: "PATCH", body: { fromEmail, replyTo }, token });

// emailRevision: the last-seen campaign.emailRevision Step 9 loaded --
// backend refuses (EMAIL_CHANGED) if the email was edited since (spec
// §31-32, "empêcher l'envoi d'une ancienne version"). Optional -- omitting
// it skips the check server-side, but every real caller should pass it.
export const confirmSendCampaignAPI = (id, emailRevision, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/confirm-send`, { method: "POST", body: { emailRevision }, token });

// scheduledAt: an ISO date string in the future -- backend re-validates
// `> now` regardless (spec: client-side is convenience only).
export const scheduleCampaignAPI = (id, scheduledAt, emailRevision, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/schedule`, { method: "PATCH", body: { scheduledAt, emailRevision }, token });

export const cancelCampaignAPI = (id, token) => apiFetch(`/email-marketing/campaigns/${id}/cancel`, { method: "POST", token });

// ---- History & Analytics --------------------------------------------------

export const getCampaignAnalyticsAPI = (id, token) => apiFetch(`/email-marketing/campaigns/${id}/analytics`, { token });

// Per-recipient delivery report (spec: "if some emails aren't ok, still
// send the good ones, and report which were sent vs which weren't") --
// getCampaignAnalyticsAPI above only returns aggregate counts; this lists
// the actual per-recipient rows (email per the viewer's own role
// visibility, status, lastError, timestamps), optionally filtered to one
// status (e.g. "failed").
export const getCampaignRecipientReportAPI = (id, { status = "all", page = 1, limit = 50 } = {}, token) => {
  const query = buildQuery({ status, page, limit });
  return apiFetch(`/email-marketing/campaigns/${id}/recipients${query ? `?${query}` : ""}`, { token });
};

export const getCampaignAuditLogAPI = (id, token) => apiFetch(`/email-marketing/campaigns/${id}/audit-log`, { token });

// ---- Visual Content & Product Display (visual redesign) ------------------
// previewProductDisplayAPI reuses the exact renderer real campaigns use --
// no separate "web-only" preview approximation (spec §21, §51).

export const previewProductDisplayAPI = (displayConfig, token) =>
  apiFetch("/email-marketing/visual-settings/preview-product", { method: "POST", body: { displayConfig }, token });

// ---- Marketing Image Library + Slider/Banner Library ----------------------

export const listMarketingAssetsAPI = (params = {}, token) => {
  const query = buildQuery(params);
  return apiFetch(`/email-marketing/assets${query ? `?${query}` : ""}`, { token });
};

export const createMarketingAssetAPI = (payload, token) =>
  apiFetch("/email-marketing/assets", { method: "POST", body: payload, token });

export const updateMarketingAssetAPI = (id, payload, token) =>
  apiFetch(`/email-marketing/assets/${id}`, { method: "PATCH", body: payload, token });

export const archiveMarketingAssetAPI = (id, token) =>
  apiFetch(`/email-marketing/assets/${id}/archive`, { method: "POST", token });

export const restoreMarketingAssetAPI = (id, token) =>
  apiFetch(`/email-marketing/assets/${id}/restore`, { method: "POST", token });

// ---- Social Media Marketing (Publish & Share) -----------------------------
// Authenticated settings management only -- the public page itself is
// /elakay_api/marketing/:id (no token, see server_elakay/elakay_routes/
// marketingPublic.js), never fetched through this authenticated apiFetch.

export const getCampaignSocialShareAPI = (id, token) => apiFetch(`/email-marketing/campaigns/${id}/social`, { token });

export const saveCampaignSocialShareAPI = (id, payload, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/social`, { method: "PATCH", body: payload, token });

export const publishCampaignSocialShareAPI = (id, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/social/publish`, { method: "POST", token });

export const republishCampaignSocialShareAPI = (id, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/social/republish`, { method: "POST", token });

export const unpublishCampaignSocialShareAPI = (id, token) =>
  apiFetch(`/email-marketing/campaigns/${id}/social/unpublish`, { method: "POST", token });
